import React, { useState, useEffect } from 'react';
import {
  getAllLanguages,
  createLanguage,
  updateLanguage,
  deleteLanguage,
  setDefaultLanguage
} from '../services/languages';
import './LanguageManagement.css';

// 預設的翻譯文字 keys
const DEFAULT_TRANSLATION_KEYS = {
  // UI 控制
  'ui.start': '開始',
  'ui.pause': '暫停',
  'ui.resume': '繼續',
  'ui.stop': '停止',
  'ui.reset': '重置',
  'ui.apply': '套用',
  'ui.cancel': '取消',
  'ui.save': '儲存',
  'ui.delete': '刪除',
  'ui.edit': '編輯',
  'ui.add': '新增',
  'ui.close': '關閉',
  'ui.confirm': '確認',
  'ui.back': '返回',

  // 計時器顯示 - 簡易版
  'timer.simple.levelName': '區間名稱',
  'timer.simple.countdown': '倒數',
  'timer.simple.currentBlind': '目前盲注',
  'timer.simple.nextBlind': '下個盲注',
  'timer.simple.timeToBreak': '多久下個休息',
  'timer.simple.avgChips': '均碼',
  'timer.simple.playerRank': '玩家名次',

  // 計時器顯示 - 完整版
  'timer.full.timerName': '計時器名稱',
  'timer.full.levelName': '區間名稱',
  'timer.full.countdown': '倒數計時',
  'timer.full.currentBlind': '目前盲注',
  'timer.full.nextBlind': '下個盲注',
  'timer.full.prizePool': '獎金池',
  'timer.full.totalChips': '總籌碼',
  'timer.full.avgStack': '平均籌碼',
  'timer.full.players': '玩家數',
  'timer.full.timeToBreak': '距離休息',
  'timer.full.marquee': '跑馬燈',

  // 控制面板
  'control.title': '計時器控制面板',
  'control.basicControl': '基本控制',
  'control.jumpLevel': '跳區間計時',
  'control.jumpLevelDesc': '重新計時',
  'control.selectSegment': '選擇區段',
  'control.jump': '跳轉',
  'control.timeline': '時間軸調整',
  'control.timelineProgress': '目前區段進度',
  'control.manualApply': '手動套用',
  'control.currentSegment': '目前區段',
  'control.segmentDuration': '區段時長',
  'control.bubbleTime': '泡泡時間',
  'control.bubbleTimeActive': '泡泡時間進行中',
  'control.bubbleMode': '模式',
  'control.bubbleSingleHand': '單手時間（秒）',
  'control.bubbleHandCount': '手數',
  'control.bubbleStart': '啟動泡泡時間',
  'control.bubbleStop': '停止泡泡時間',

  // 狀態
  'status.active': '進行中',
  'status.paused': '已暫停',
  'status.completed': '已完成',
  'status.pending': '待開始',
  'status.stopped': '已停止',

  // 訊息
  'message.loading': '載入中...',
  'message.error': '錯誤',
  'message.success': '成功',
  'message.confirmDelete': '確定要刪除嗎？',
  'message.confirmStop': '確定要手動結束計時器嗎？',
  'message.confirmReset': '確定要重置計時器嗎？這將清除所有進度。',
  'message.timerNotStarted': '計時器尚未啟動',
  'message.selectSegment': '請選擇區段',
  'message.timeApplied': '時間已套用',

  // 其他
  'common.minutes': '分',
  'common.seconds': '秒',
  'common.hours': '小時',
  'common.bb': 'BB',
  'common.ante': '底注',
};

function LanguageManagement() {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLang, setEditingLang] = useState(null);
  const [newLangName, setNewLangName] = useState('');
  const [newLangCode, setNewLangCode] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);

  // Load languages
  useEffect(() => {
    loadLanguages();
  }, []);

  const loadLanguages = async () => {
    try {
      const langs = await getAllLanguages();
      setLanguages(langs);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load languages:', err);
      alert('載入語系失敗：' + err.message);
      setLoading(false);
    }
  };

  // Add new language
  const handleAddLanguage = async () => {
    if (!newLangName.trim()) {
      alert('請輸入語系名稱');
      return;
    }

    if (!newLangCode.trim()) {
      alert('請輸入語系代碼（例如：en, zh-TW）');
      return;
    }

    try {
      // Create new language with default translations (empty values)
      const translations = {};
      Object.keys(DEFAULT_TRANSLATION_KEYS).forEach(key => {
        translations[key] = ''; // Empty by default, user will fill in
      });

      const newLang = {
        name: newLangName,
        code: newLangCode,
        translations: translations
      };

      await createLanguage(newLang);
      alert('語系新增成功！');
      setNewLangName('');
      setNewLangCode('');
      setShowAddDialog(false);
      loadLanguages();
    } catch (err) {
      alert('新增失敗：' + err.message);
    }
  };

  // Delete language
  const handleDeleteLanguage = async (langId, langName) => {
    if (!window.confirm(`確定要刪除「${langName}」語系嗎？`)) {
      return;
    }

    try {
      await deleteLanguage(langId);
      alert('刪除成功！');
      loadLanguages();
    } catch (err) {
      alert('刪除失敗：' + err.message);
    }
  };

  // Set as default language
  const handleSetDefault = async (langId) => {
    try {
      await setDefaultLanguage(langId);
      alert('預設語系已設定！');
      loadLanguages();
    } catch (err) {
      alert('設定失敗：' + err.message);
    }
  };

  // Edit language translations
  const handleEditLanguage = (lang) => {
    setEditingLang({ ...lang });
  };

  // Save edited translations
  const handleSaveTranslations = async () => {
    if (!editingLang) return;

    try {
      await updateLanguage(editingLang._id, {
        translations: editingLang.translations
      });
      alert('翻譯已儲存！');
      setEditingLang(null);
      loadLanguages();
    } catch (err) {
      alert('儲存失敗：' + err.message);
    }
  };

  // Update translation value
  const handleTranslationChange = (key, value) => {
    setEditingLang({
      ...editingLang,
      translations: {
        ...editingLang.translations,
        [key]: value
      }
    });
  };

  if (loading) {
    return <div className="language-management-loading">載入中...</div>;
  }

  return (
    <div className="language-management">
      <div className="language-header">
        <h1>多語言管理</h1>
        <button className="btn btn-primary" onClick={() => setShowAddDialog(true)}>
          ➕ 新增語系
        </button>
      </div>

      {/* Add Language Dialog */}
      {showAddDialog && (
        <div className="dialog-overlay" onClick={() => setShowAddDialog(false)}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            <h2>新增語系</h2>
            <input
              type="text"
              placeholder="請輸入語系名稱（例如：English、日本語）"
              value={newLangName}
              onChange={(e) => setNewLangName(e.target.value)}
              className="language-name-input"
            />
            <input
              type="text"
              placeholder="請輸入語系代碼（例如：en、ja、zh-TW）"
              value={newLangCode}
              onChange={(e) => setNewLangCode(e.target.value)}
              className="language-name-input"
              style={{ marginTop: '10px' }}
            />
            <div className="dialog-actions">
              <button className="btn btn-secondary" onClick={() => setShowAddDialog(false)}>
                取消
              </button>
              <button className="btn btn-primary" onClick={handleAddLanguage}>
                新增
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Translation Dialog */}
      {editingLang && (
        <div className="dialog-overlay" onClick={() => setEditingLang(null)}>
          <div className="dialog-content translation-editor" onClick={(e) => e.stopPropagation()}>
            <h2>編輯翻譯 - {editingLang.name}</h2>
            <div className="translation-list">
              {Object.entries(DEFAULT_TRANSLATION_KEYS).map(([key, defaultValue]) => (
                <div key={key} className="translation-item">
                  <div className="translation-key">
                    <label>{key}</label>
                    <span className="default-value">預設：{defaultValue}</span>
                  </div>
                  <input
                    type="text"
                    value={editingLang.translations[key] || ''}
                    onChange={(e) => handleTranslationChange(key, e.target.value)}
                    placeholder={`輸入 ${editingLang.name} 翻譯`}
                    className="translation-input"
                  />
                </div>
              ))}
            </div>
            <div className="dialog-actions">
              <button className="btn btn-secondary" onClick={() => setEditingLang(null)}>
                取消
              </button>
              <button className="btn btn-primary" onClick={handleSaveTranslations}>
                儲存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Language List */}
      <div className="language-list">
        {languages.length === 0 ? (
          <div className="empty-state">
            <p>尚未建立任何語系</p>
            <p>點擊「新增語系」開始建立</p>
          </div>
        ) : (
          languages.map((lang) => (
            <div key={lang._id} className="language-card">
              <div className="language-info">
                <h3>
                  {lang.name} ({lang.code})
                  {lang.isDefault && <span className="default-badge">預設</span>}
                </h3>
                <p className="translation-count">
                  已翻譯：{Object.values(lang.translations || {}).filter(v => v).length} / {Object.keys(DEFAULT_TRANSLATION_KEYS).length}
                </p>
              </div>
              <div className="language-actions">
                {!lang.isDefault && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleSetDefault(lang._id)}
                  >
                    設為預設
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={() => handleEditLanguage(lang)}
                >
                  編輯翻譯
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => handleDeleteLanguage(lang._id, lang.name)}
                >
                  刪除
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default LanguageManagement;
